import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";
const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];

// working_hours[day] may be a single {start,end} (legacy) or an array of {start,end} (split shifts).
const toWindows = (v) => (Array.isArray(v) ? v : v && v.start ? [v] : []);

// All IANA time zones the browser knows about, with a curated fallback for older engines.
const TIMEZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
  } catch (e) { /* fall through */ }
  return [
    "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Anchorage", "America/Phoenix", "America/Toronto", "America/Mexico_City", "America/Sao_Paulo",
    "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Rome", "Europe/Athens",
    "Europe/Moscow", "Africa/Cairo", "Africa/Johannesburg", "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok",
    "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul", "Australia/Sydney",
    "Pacific/Auckland",
  ];
})();

export default function StaffAvailability() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(user?.id || "");
  const [av, setAv] = useState(null);

  useEffect(() => { api.get("/scheduling/staff").then((r) => { setStaff(r.data.staff || []); if (!selected && r.data.staff?.[0]) setSelected(r.data.staff[0].id); }); }, []); // eslint-disable-line
  const load = useCallback(() => {
    if (!selected) return;
    api.get(`/scheduling/availability/${selected}`).then((r) => {
      const data = r.data || {};
      const wh = {};
      Object.entries(data.working_hours || {}).forEach(([d, v]) => { wh[d] = toWindows(v); });
      setAv({ ...data, working_hours: wh });
    }).catch(() => {});
  }, [selected]);
  useEffect(() => { load(); }, [load]);

  const windows = (d) => av?.working_hours?.[d] || [];
  const setWin = (d, i, k, v) => setAv((s) => {
    const wins = [...(s.working_hours[d] || [])]; wins[i] = { ...wins[i], [k]: v };
    return { ...s, working_hours: { ...s.working_hours, [d]: wins } };
  });
  const addWin = (d) => setAv((s) => {
    const wins = [...(s.working_hours[d] || [])];
    wins.push(wins.length ? { start: "14:00", end: "17:00" } : { start: "09:00", end: "17:00" });
    return { ...s, working_hours: { ...s.working_hours, [d]: wins } };
  });
  const removeWin = (d, i) => setAv((s) => {
    const wins = (s.working_hours[d] || []).filter((_, x) => x !== i);
    return { ...s, working_hours: { ...s.working_hours, [d]: wins } };
  });
  const toggleDay = (d) => setAv((s) => {
    const wh = { ...s.working_hours };
    if ((wh[d] || []).length) wh[d] = [];
    else wh[d] = [{ start: "09:00", end: "17:00" }];
    return { ...s, working_hours: wh };
  });

  const save = async () => {
    try {
      // strip empty days so they read as "off"
      const wh = {}; Object.entries(av.working_hours).forEach(([d, v]) => { if (v && v.length) wh[d] = v; });
      await api.put(`/scheduling/availability/${selected}`, { ...av, working_hours: wh });
      toast.success("Availability saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-[900px] mx-auto space-y-4" data-testid="staff-availability">
      <div><h1 className="text-2xl font-bold text-slate-900">Staff Availability</h1><p className="text-sm text-slate-500">Working hours (with split shifts), lunch, buffers, time off and limits — the engine respects these when suggesting slots.</p></div>
      <div><label className={lbl}>Staff member</label><select className={inp + " max-w-sm"} value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="avail-staff-select">{staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}</select></div>
      {!av ? <div className="py-8 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Time zone</label>
              <select className={inp} value={av.timezone || ""} onChange={(e) => setAv({ ...av, timezone: e.target.value })} data-testid="avail-timezone">
                <option value="">Select a time zone…</option>
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
                {av.timezone && !TIMEZONES.includes(av.timezone) && <option value={av.timezone}>{av.timezone.replace(/_/g, " ")}</option>}
              </select>
            </div>
            <div><label className={lbl}>Working location</label><input className={inp} value={av.working_location || ""} onChange={(e) => setAv({ ...av, working_location: e.target.value })} /></div>
          </div>
          <div>
            <label className={lbl}>Working hours — add multiple time blocks per day for split shifts</label>
            <div className="space-y-2">
              {DAYS.map(([d, name]) => {
                const wins = windows(d);
                const on = wins.length > 0;
                return (
                  <div key={d} className="flex items-start gap-3 py-1.5 border-b border-slate-50 last:border-0">
                    <label className="flex items-center gap-2 w-32 pt-1 text-sm text-slate-600 shrink-0"><input type="checkbox" checked={on} onChange={() => toggleDay(d)} className="accent-blue-600" data-testid={`avail-day-${d}`} />{name}</label>
                    {on ? (
                      <div className="flex-1 space-y-1.5">
                        {wins.map((w, i) => (
                          <div key={i} className="flex items-center gap-2" data-testid={`avail-window-${d}-${i}`}>
                            <input type="time" className="border border-slate-300 rounded-lg px-2 py-1 text-sm" value={w.start} onChange={(e) => setWin(d, i, "start", e.target.value)} data-testid={`avail-${d}-${i}-start`} />
                            <span className="text-slate-400 text-sm">to</span>
                            <input type="time" className="border border-slate-300 rounded-lg px-2 py-1 text-sm" value={w.end} onChange={(e) => setWin(d, i, "end", e.target.value)} data-testid={`avail-${d}-${i}-end`} />
                            <button onClick={() => removeWin(d, i)} className="text-slate-400 hover:text-red-500" data-testid={`avail-${d}-${i}-remove`} title="Remove block"><Icons.X className="h-4 w-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => addWin(d)} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid={`avail-${d}-add`}><Icons.Plus className="h-3.5 w-3.5" />Add time block</button>
                      </div>
                    ) : <span className="text-xs text-slate-400 pt-1">Off</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div><label className={lbl}>Lunch start</label><input type="time" className={inp} value={av.lunch?.start || "12:00"} onChange={(e) => setAv({ ...av, lunch: { ...av.lunch, start: e.target.value } })} /></div>
            <div><label className={lbl}>Lunch end</label><input type="time" className={inp} value={av.lunch?.end || "13:00"} onChange={(e) => setAv({ ...av, lunch: { ...av.lunch, end: e.target.value } })} /></div>
            <div><label className={lbl}>Buffer between appts (min)</label><input type="number" min="0" step="5" className={inp} value={av.buffer_min ?? 0} onChange={(e) => setAv({ ...av, buffer_min: Number(e.target.value) || 0 })} data-testid="avail-buffer" /></div>
            <div><label className={lbl}>Max meetings/day</label><input type="number" className={inp} value={av.max_meetings_per_day || ""} onChange={(e) => setAv({ ...av, max_meetings_per_day: Number(e.target.value) || null })} /></div>
          </div>
          <p className="text-xs text-slate-400 -mt-1">Buffer adds automatic gap time before &amp; after every appointment so slots never touch back-to-back.</p>
          <button onClick={save} data-testid="avail-save" className="bg-[#1f6feb] text-white rounded-lg px-4 py-2 text-sm font-medium">Save Availability</button>
        </div>
      )}
    </div>
  );
}
